import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import friendService from '../../services/friendService';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

/**
 * Close friends — one state per person, shared by every mounted surface.
 * docs: ~/Downloads handoff "Close Friends — Mirror Room" (option 2b).
 *
 * Each surface loads `isClose` with its own data (friends list, search, people
 * search). Once someone is toggled in this session the store's value wins, so
 * lighting Maya on the People grid lights her in the search dropdown too. The
 * room plays only on the instance that was clicked: `rooms` holds its owner.
 */
export const ROOM_MS = 2700;
export const FLASH_MS = 2200;
export const SAVE_ERROR = "Couldn't save. Try again.";

const closeById = new Map(); // userId → boolean, set by toggles this session
const rooms = new Map(); // userId → { owner, start, token }
const seqById = new Map(); // userId → latest request number (stale replies don't revert)
const listeners = new Set();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}
function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}
const getVersion = () => version;

let tokens = 0;
function startRoom(userId, owner) {
  tokens += 1;
  rooms.set(userId, { owner, start: performance.now(), token: tokens });
}

/** Test seam: forget everything toggled this session. */
export function resetCloseFriendsStore() {
  closeById.clear();
  rooms.clear();
  seqById.clear();
  emit();
}

/**
 * @param {string} userId   the friend's user id
 * @param {boolean} serverIsClose  what the surface's own payload says
 * @param {string} name     display name, for the room's sub-line
 * @returns {{ on, q, playing, flash, error, toggle, first }}
 *   q       — room progress 0→1 while this instance's room plays, else null
 *   flash   — the card's CLOSE FRIEND · THEY'LL KNOW line is showing
 *   error   — the save failed (cleared after FLASH_MS)
 */
export default function useCloseFriend(userId, serverIsClose, name = '') {
  const instance = useId();
  useSyncExternalStore(subscribe, getVersion, getVersion);
  const reduced = usePrefersReducedMotion();

  const on = closeById.has(userId) ? closeById.get(userId) : !!serverIsClose;
  const room = rooms.get(userId);
  const mine = room && room.owner === instance ? room : null;

  const [now, setNow] = useState(0);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState('');
  const timers = useRef([]);
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => clearTimers, []);

  const showFlash = useCallback(() => {
    setFlash(true);
    later(() => setFlash(false), FLASH_MS);
  }, []);

  // One rAF loop per playing room; everything else derives from q.
  const token = mine?.token;
  useEffect(() => {
    if (!token) return undefined;
    const r = rooms.get(userId);
    let raf;
    const tick = () => {
      const t = performance.now();
      setNow(t);
      if (t - r.start < ROOM_MS) { raf = requestAnimationFrame(tick); return; }
      if (rooms.get(userId)?.token === token) { rooms.delete(userId); emit(); }
      // The status line speaks once the room has closed, so it's actually seen.
      showFlash();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [token, userId, showFlash]);

  const q = mine ? Math.max(0, Math.min(1, (now - mine.start) / ROOM_MS)) : null;

  const toggle = useCallback(async () => {
    const next = !on;
    clearTimers();
    setFlash(false);
    setError('');
    closeById.set(userId, next);
    rooms.delete(userId); // any room for this person, on any surface, stops
    if (next) {
      if (reduced) showFlash();
      else { startRoom(userId, instance); setNow(performance.now()); }
    }
    emit();

    const seq = (seqById.get(userId) || 0) + 1;
    seqById.set(userId, seq);
    const r = await friendService.setClose(userId, next);
    if (r?.success !== false || seqById.get(userId) !== seq) return;
    closeById.set(userId, !next);
    rooms.delete(userId);
    emit();
    clearTimers();
    setFlash(false);
    setError(SAVE_ERROR);
    later(() => setError(''), FLASH_MS);
  }, [on, userId, instance, reduced, showFlash]);

  const first = (String(name || '').trim().split(/\s+/)[0] || 'They').toUpperCase();

  return { on, q, playing: q != null, flash, error, toggle, first };
}
